import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext.js';
import './LanguageSwitcher.css';

const LanguageSwitcher = ({
  isOpen: controlledIsOpen,
  onToggle,
  onClose,
  className = '',
  variant = 'header',
}) => {
  const { language, setLanguage, languages, currentLanguage, t } = useLanguage();
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const [align, setAlign] = useState('right');
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const isControlled = typeof controlledIsOpen === 'boolean';
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;
  const selected = languages.find(lang => lang.code === language) ?? languages[0];
  const orderedLanguages = selected
    ? [selected, ...languages.filter(lang => lang.code !== selected.code)]
    : languages;

  const handleToggle = () => {
    const nextState = !isOpen;
    if (!isControlled) setUncontrolledIsOpen(nextState);
    if (onToggle) onToggle(nextState);
  };

  const handleClose = () => {
    if (!isControlled) setUncontrolledIsOpen(false);
    if (onClose) onClose();
  };

  const handleSelectLanguage = langCode => {
    setLanguage(langCode);
    handleClose();
  };

  useLayoutEffect(() => {
    if (!isOpen) return;

    const measure = () => {
      const container = containerRef.current;
      const dropdown = dropdownRef.current;
      if (!container || !dropdown) return;

      const containerRect = container.getBoundingClientRect();
      const dropdownWidth = dropdown.offsetWidth;
      const viewportWidth = window.innerWidth;
      const margin = 8;

      const spaceRight = viewportWidth - containerRect.left;
      const spaceLeft = containerRect.right;
      const fitsRight = spaceRight >= dropdownWidth + margin;
      const fitsLeft = spaceLeft >= dropdownWidth + margin;

      // Prefer anchoring to the button's inline-end edge (its left edge in RTL).
      const isRtl = document.documentElement.dir === 'rtl';
      if (isRtl ? fitsRight : fitsLeft) {
        setAlign(isRtl ? 'left' : 'right');
      } else if (isRtl ? fitsLeft : fitsRight) {
        setAlign(isRtl ? 'right' : 'left');
      } else {
        setAlign(spaceRight >= spaceLeft ? 'left' : 'right');
      }
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div
      className={`language-switcher language-switcher--${variant} ${className}`.trim()}
      ref={containerRef}
    >
      <button
        type="button"
        className={`language-switcher__btn${isOpen ? ' language-switcher__btn--open' : ''}`}
        onClick={handleToggle}
        title={t('header.language')}
        aria-label={t('header.language')}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="language-switcher__name">
          {currentLanguage?.name || language}
        </span>
        <ChevronDown
          className={`language-switcher__chevron${isOpen ? ' language-switcher__chevron--open' : ''}`}
          size={16}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={`language-switcher__dropdown language-switcher__dropdown--${align}`}
          role="listbox"
          aria-label={t('header.language')}
        >
          <div className="language-switcher__dropdown-menu">
            {orderedLanguages.map(lang => {
              const isActive = language === lang.code;
              return (
                <button
                  key={lang.code}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`language-switcher__item${isActive ? ' language-switcher__item--active' : ''}`}
                  onClick={() => handleSelectLanguage(lang.code)}
                >
                  <span className="language-switcher__item-title">{lang.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

LanguageSwitcher.propTypes = {
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  onClose: PropTypes.func,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['header', 'surface']),
};

export default LanguageSwitcher;
